import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, unref as _unref, createVNode as _createVNode, withCtx as _withCtx, createBlock as _createBlock, Fragment as _Fragment } from "vue"

const _hoisted_1 = ["innerHTML"]

import {
  computed,
  type Ref,
  nextTick,
  onBeforeMount,
  onBeforeUnmount,
  ref,
  watch,
  onMounted,
} from "vue";
// Components
import ContentWrapper from "./ContentWrapper.vue";
import PopupReviewPost from "./components/PopupReviewPost/PopupReviewPost.vue";
import PopupReviewPostMobile from "./components/PopupReviewPost/PopupReviewPostMobile.vue";
import PopupReviewText from "./components/PopupReviewPost/PopupReviewText.vue";
import FullyWidgetContainerPopup from "./components/FullyWidgetContainer/FullyWidgetContainer.vue";
// Api
import {
  getCustomersMediaList,
  getPostsInfo,
  TCustomersMediaList,
  TPostsFilterOptions,
} from "../../api/api";
// Services
import { addSchemaToPage } from "@/common/services/SchemaOrg/SchemaOrg";
import { sendToGa4Dictionary } from "../../../../helpers.js";
import { sendFrisbuyAnalyticsEvent } from "@/common/services/FrisbuyAnalytics/FrisbuyAnalytics";
// Hooks
import usePostExtractor from "./hooks/postExtractor";
// Types
import {
  imageWithPostId,
  IMarketplaceReviewsPost,
  productRating,
} from "./interfaces";
import widgetTypes from "@/common/consts/widgetTypes.js";

//#region Schema.org
type TStateKey = "initial" | "modal";
type TWidgetState = {
  propPostList: Ref<IMarketplaceReviewsPost[]>;
  postCount: Ref<number>;
  allCustomersMediaList: Ref<Array<imageWithPostId>>;
  postListWithImages: Ref<IMarketplaceReviewsPost[]>;
  bestPostIds: Ref<number[]>;
  customersMediaTotalCount: Ref<number | null>;
  customersMediaScrollPage: Ref<number>;
  loadCustomersMediaErrorsCount: Ref<number>;
  hasActiveRequest: Ref<boolean>;
  requestedCustomersMediaCursorSet: Set<string>;
  mediaSort: Ref<{
    orderBy: "provider_created" | "rating" | "best";
    sort: "asc" | "desc";
    current_variant?: "true";
  }>;
};

const maxLoadCustomersMediaErrorsCount = 5;


export default /*#__PURE__*/_defineComponent({
  __name: 'MarketplaceReviews',
  props: {
    settings: {},
    mobileSettings: {},
    propPostList: {},
    lastPage: {},
    propPostCount: {},
    productRating: {},
    extraData: {}
  },
  setup(__props: any) {

// Modules
onBeforeMount(() => {
  if (!props.productRating || props.productRating.reviewsCount <= 0) {
    return;
  }

  const bestRating = 5;
  // NOTE: if ratingValue === 0 and worstRating === 1, then will be error
  const worstRating = 1;
  const product = props.productRating.sku[0];

  const reviews = filteredPostList.value.map((post) => {
    const ratingValue =
      post.frisbuyPost.rating > 0 ? post.frisbuyPost.rating : bestRating;

    return {
      "@type": "Review",

      author: {
        "@type": "Person",
        name:
          post.instagramPost.authorUser.fio ||
          `@${post.instagramPost.authorUser.username}`,
      },
      datePublished: postExtractor.getParsedReviewDate(post),
      reviewBody: `${post.frisbuyPost.text}\n${post.frisbuyPost.positiveComment}\n${post.frisbuyPost.negativeComment}`,

      reviewRating: {
        "@type": "Rating",
        bestRating: bestRating,
        ratingValue: ratingValue,
        worstRating: worstRating,
      },
    };
  });

  const productAvgRating =
    props.productRating.avgRating > 0
      ? props.productRating.avgRating
      : bestRating;

  const SchemaorgProduct = {
    "@context": "https://schema.org/",
    "@type": "Product",
    name: product.name,
    image: product.thumbUrl,
    aggregateRating: {
      "@type": "AggregateRating",
      ratingValue: productAvgRating,
      bestRating: bestRating,
      worstRating: worstRating,
      reviewCount: props.productRating.reviewsCount,
    },
    offers: {
      "@type": "AggregateOffer",
      url: product.vendorShopUrl,
      priceCurrency: product.currencyCode,
      price: product.price,
      offerCount: props.productRating.allReviewsCount,
    },
    review: reviews,
  };

  addSchemaToPage(SchemaorgProduct);
});
//#endregion Schema.org

const props = __props;

const postExtractor = usePostExtractor();

const hadSeen = ref(false);
const inViewTimeout = ref<number | undefined>(undefined);
const had5SecInView = ref(false);
const isShowFullyWidget = ref(false);

const postList = ref<IMarketplaceReviewsPost[]>(props.propPostList);

const filteredPostList = computed<IMarketplaceReviewsPost[]>(() => {
  const filtered = postList.value.slice(
    (selectedPage.value - 1) * perPage,
    selectedPage.value * perPage
  );

  return filtered;
});

function isElementInView(element: any, isFull = false) {
  if (!element) return;

  const { top, bottom, height } = element.getBoundingClientRect();
  const { innerHeight: windowHeight } = window;

  if (isFull) {
    return (
      (height > windowHeight && bottom > 0 && bottom < windowHeight) ||
      (top > 0 && bottom < windowHeight)
    );
  } else {
    return top < windowHeight;
  }
}

function scrollHandler() {
  const InView5SecHandler = () => {
    had5SecInView.value = true;
    sendAnaliticEvent("Eyesight5Sec", window.location.href, true, true);
  };

  nextTick(() => {
    const firstItem = widgetMarketplaceReviews.value?.querySelector(
      ".frisbuy-marketplace-reviews__list-item"
    );
    if (
      !had5SecInView.value &&
      isElementInView(widgetMarketplaceReviews.value, true)
    ) {
      window.clearTimeout(inViewTimeout.value);

      inViewTimeout.value = setTimeout(InView5SecHandler, 5000);
    } else {
      window.clearTimeout(inViewTimeout.value);
    }

    if (
      !hadSeen.value &&
      isElementInView(firstItem || widgetMarketplaceReviews.value, true)
    ) {
      hadSeen.value = true;
      sendAnaliticEvent("Eyesight", window.location.href, true, true);
    }
  });
}

function sendGAEvent(
  action: any,
  label: any,
  sendToClient: any,
  isNonInteraction: any
) {
  const originalAction = action;

  const { settings } = props;

  const { categoryPrefix, actionPrefix, trackingSkuId, trackingTagCode } =
    settings.analytics;
  const { analytics } = settings;
  const category = `Frisbuy_Gallery_${categoryPrefix + settings.customerCode}`;
  const labelItemPosition = [
    "fbProduct",
    "igCode",
    "slide",
    "link",
    "linklabel",
    "product",
    "variation",
    "offer",
    "index",
    "swipeType",
    "shareType",
    "queued",
    "load",
    "bundle",
    "domInject",
    "filter",
  ];
  const labelString =
    typeof label === "object"
      ? labelItemPosition.reduce((labelString, currentLabelItem) => {
          if (!Object.hasOwn(label, currentLabelItem)) return labelString;

          return (
            (labelString ? labelString + "_" : labelString) +
            currentLabelItem +
            label[currentLabelItem]
          );
        }, "")
      : label;

  if (!action.includes("ClickMainLink")) {
    action = `${action}_gallery${settings.galleryId}_embedID${
      settings.embedId + actionPrefix + trackingSkuId + trackingTagCode
    }`;
  }

  if (action.includes("FilterElastic")) {
    const { elasticFilter } = settings.analytics;

    let filterValue = 0;

    if (elasticFilter) {
      filterValue = elasticFilter.elasticTitle;
    }

    action = `FilterElastic_gallery${settings.galleryId}_embedID${
      settings.embedId
    }_filter${filterValue + actionPrefix + trackingSkuId + trackingTagCode}`;
  }

  if (settings.analytics?.elasticFilter && !action.includes("FilterElastic")) {
    const { gender, color, categoryId } = settings.analytics.elasticFilter;

    action += `_cat${categoryId}_gender${gender}_color${color}`;
  }

  if (settings.analytics?.ga.sendToGa) {
    if (settings.verbose) {
      console.log(
        `%cSend event: %c${category} %c${action} %c${labelString}`,
        "color: red; font-weight: bold",
        "color: blue",
        "color: green",
        "color: darkcyan"
      );
    }

    // GA4
    if (window.frisbuyGa4) {
      sendToGa4Dictionary(
        originalAction,
        { settings: props.settings },
        label,
        true,
        widgetTypes.MARKETPLACE_REVIEW
      );
    }
  }

  if (!sendToClient) return;

  if (analytics?.gtm && analytics?.gtm.eventName) {
    const { gtm } = analytics;
    const gtmPayload = Object.assign(gtm.static || {}, {
      event: gtm.eventName,
      [gtm.eventCategory]: "Frisbuy_Gallery",
      [gtm.eventAction]: action,
      [gtm.eventLabel]: labelString,
      [gtm.eventNonInteractive]: isNonInteraction,
    });

    window.frisbuyGTM(gtmPayload);
  }

  if (analytics?.ga.sendToClient) {
    if (settings.verbose) {
      console.log(
        `${window["GoogleAnalyticsObject"]}('send', 'event', 'Frisbuy_Gallery', '${action}', '${labelString}')`
      );
    }

    if (window.frisbuyGa4) {
      sendToGa4Dictionary(
        originalAction,
        { settings: props.settings },
        label,
        false,
        widgetTypes.MARKETPLACE_REVIEW
      );
    }
  }
}

function sendYMEvent(action: any, label: any) {
  const {
    galleryId: gallery,
    embedId: embedID,
    skuId,
    tagCode,
  } = props.settings;
  const clientCounterId = props.settings.analytics?.ym.clientCounterId;
  const conunterId = `yaCounter${clientCounterId}`;
  const ymParams = typeof label === "object" ? label : {};

  if (!clientCounterId || !window[conunterId]) {
    return;
  }

  Object.assign(ymParams, {
    gallery,
    embedID,
    widgetType: widgetTypes.MARKETPLACE_REVIEW,
  });

  if (skuId) {
    ymParams.sku = skuId;
  }

  if (tagCode) {
    ymParams.tagCode = tagCode;
  }

  if (ymParams.linklabel) {
    ymParams.linklabel = ymParams.linklabel.replace(/[^\w]+/g, "+");
  }

  window[conunterId].reachGoal(`Frisbuy${action}`, ymParams, () => {
    if (!props.settings.verbose) return;

    console.log(conunterId + ".push", `Frisbuy${action}`, ymParams);
  });
}

function sendAnaliticEvent(
  action: any,
  label: any,
  sendToClient = true,
  isNonInteraction = false
) {
  sendGAEvent(action, label, sendToClient, isNonInteraction);
  sendYMEvent(action, label);

  // [START] Frisbuy Analytics
  const frisbuyParams = {
    srcDomain: props.settings.srcDomain,

    embedId: props.settings.embedId,
    widgetType: widgetTypes.MARKETPLACE_REVIEW,
    galleryId: props.settings.galleryId,
    skuId: props.settings.skuId,
    skuGroupId: props.settings.groupId,
    categoryId: props.settings.categoryId,
    tagCode: props.settings.tagCode,
    frUId: props.settings.frisbuyUID,
  };

  // TODO: check `label` on correct data
  if (typeof label === "object") {
    Object.assign(frisbuyParams, label);
  }

  sendFrisbuyAnalyticsEvent(action, frisbuyParams);
  // [END] Frisbuy Analytics
}

function createWidgetState(
  initialPosts: IMarketplaceReviewsPost[],
  postCount: number
): TWidgetState {
  return {
    propPostList: ref(initialPosts),
    postCount: ref(postCount),
    allCustomersMediaList: ref([]),
    postListWithImages: ref([]),
    bestPostIds: ref([]),
    customersMediaTotalCount: ref(null),
    customersMediaScrollPage: ref(1),
    loadCustomersMediaErrorsCount: ref(0),
    hasActiveRequest: ref(false),
    requestedCustomersMediaCursorSet: new Set<string>(),
    mediaSort: ref({
      orderBy: "provider_created",
      sort: "desc",
      current_variant: undefined,
    }),
  };
}

const initialState = createWidgetState(props.propPostList, props.propPostCount);
const modalState = createWidgetState([], 0);
initialState.mediaSort.value.orderBy = "best";
modalState.mediaSort.value.sort = "desc";
const activeStateKey = ref<TStateKey>("initial");
const activeState = computed(() =>
  activeStateKey.value === "modal" ? modalState : initialState
);
const isModalStateLoaded = ref(false);
const isModalStateLoading = ref(false);

const customersPhotoScrollContainer = ref<HTMLElement | null>(null);

onMounted(() => {
  updateAllCustomersMediaList("initial").then(() => {
    nextTick(() => {
      // NOTE: need for fill all visible container with posts
      loadCustomersMediaOnScroll(
        "initial",
        customersPhotoScrollContainer.value,
        0.8
      );
    });
  });

  scrollHandler();
  window.addEventListener("scroll", scrollHandler);

  const { embedId, widgetStart } = props.settings;
  const entries = (performance && performance.getEntries()) || null;
  const times: any = {};

  if (entries) {
    if (widgetStart) {
      times.domInject = (performance.now() - widgetStart).toFixed(2);
    }

    entries.forEach((entry) => {
      if (entry.name.includes(embedId)) {
        times.queued = entry.startTime.toFixed(2);
        times.load = entry.duration.toFixed(2);
      } else if (
        entry.name.includes("www.frisbuy.ru") &&
        entry.name.includes("compiled")
      ) {
        times.bundle = entry.duration.toFixed(2);
      }
    });

    sendAnaliticEvent("TimeLoad", times, false, true);
  }

  sendAnaliticEvent("Load", window.location.href, true, true);

  if (props.settings.gallery.elasticFilter) {
    sendAnaliticEvent("FilterElastic", window.location.href, true, true);
  }
});

const isShowLeftScrollArrow = ref(true);
const isShowRightScrollArrow = ref(true);

function isFrisbuyMediaUrl(url?: string): boolean {
  return Boolean(url && url.toLowerCase().includes("frisbuy"));
}

function hasValidFrisbuyMedia(media?: Partial<imageWithPostId>): boolean {
  if (!media) {
    return false;
  }

  return (
    isFrisbuyMediaUrl(media.thumbnailUrl) ||
    isFrisbuyMediaUrl(media.imageUrl) ||
    isFrisbuyMediaUrl(media.imageHighResolutionUrl) ||
    isFrisbuyMediaUrl(media.videoUrl)
  );
}

function isSameMedia(left: imageWithPostId, right: imageWithPostId): boolean {
  if (left.postId !== right.postId) {
    return false;
  }

  return Boolean(
    (left.thumbnailUrl &&
      right.thumbnailUrl &&
      left.thumbnailUrl === right.thumbnailUrl) ||
      (left.imageHighResolutionUrl &&
        right.imageHighResolutionUrl &&
        left.imageHighResolutionUrl === right.imageHighResolutionUrl) ||
      (left.imageUrl && right.imageUrl && left.imageUrl === right.imageUrl) ||
      (left.videoUrl && right.videoUrl && left.videoUrl === right.videoUrl)
  );
}

function addMediaToStateWithDedup(
  state: TWidgetState,
  mediaList: imageWithPostId[]
) {
  mediaList.forEach((newMedia) => {
    if (!hasValidFrisbuyMedia(newMedia)) {
      return;
    }

    const isRealyNewMedia = !state.allCustomersMediaList.value.find((media) =>
      isSameMedia(media, newMedia)
    );

    if (isRealyNewMedia) {
      state.allCustomersMediaList.value.push(newMedia);
    }
  });
}

function mergeMediaFromPostsToState(
  stateKey: TStateKey,
  posts: IMarketplaceReviewsPost[]
) {
  const state = stateKey === "modal" ? modalState : initialState;
  const flattenedMedia: imageWithPostId[] = [];

  posts.forEach((post) => {
    const postId = post.instagramPost?.frisbuyId;
    if (typeof postId === "number") {
      const existedPostIndex = state.postListWithImages.value.findIndex(
        (statePost) => statePost?.instagramPost?.frisbuyId === postId
      );

      if (existedPostIndex < 0) {
        state.postListWithImages.value.push(post);
      } else {
        const existedPost = state.postListWithImages.value[existedPostIndex];
        const existedMediaList = Array.isArray(existedPost?.media)
          ? existedPost.media
          : [];
        const incomingMediaList = Array.isArray(post?.media) ? post.media : [];

        state.postListWithImages.value[existedPostIndex] = {
          ...existedPost,
          ...post,
          media:
            incomingMediaList.length >= existedMediaList.length
              ? incomingMediaList
              : existedMediaList,
        };
      }
    }

    const mediaPostId = post.instagramPost?.frisbuyId;
    const hasMediaFlag = Boolean(post.instagramPost?.hasMedia);
    const postMedia = Array.isArray(post.media) ? post.media : [];
    const hasRenderableMedia = postMedia.some((media) =>
      hasValidFrisbuyMedia(media)
    );

    if (!mediaPostId || !hasMediaFlag || !hasRenderableMedia) {
      return;
    }

    postMedia.forEach((media) => {
      if (!hasValidFrisbuyMedia(media)) {
        return;
      }

      flattenedMedia.push({
        ...media,
        postId: mediaPostId,
      });
    });
  });

  addMediaToStateWithDedup(state, flattenedMedia);
}

async function loadCustomersMediaOnScroll(
  stateKey: TStateKey,
  scrollEl: HTMLElement | null,
  scrollCoef = 1
) {
  const state = stateKey === "modal" ? modalState : initialState;

  if (state.hasActiveRequest.value || !scrollEl) {
    return;
  }

  //#region ScrollArrows visible
  isShowLeftScrollArrow.value = scrollEl.scrollLeft > 0;

  const scrollRight = scrollEl.scrollLeft + scrollEl.offsetWidth;
  isShowRightScrollArrow.value = scrollRight < scrollEl.scrollWidth;
  //#endregion ScrollArrows visible

  const perPageForMedia = Math.max(state.propPostList.value.length, 1);
  const maxCountOfPages = Math.ceil(state.postCount.value / perPageForMedia);

  if (!scrollEl || state.customersMediaScrollPage.value >= maxCountOfPages) {
    return;
  }

  const scrollElScrollWidth = scrollEl.scrollWidth * scrollCoef;
  const scrollLeftPos = scrollEl.scrollLeft;
  const scrollRightPos = scrollLeftPos + scrollEl.clientWidth;

  if (
    scrollRightPos >= scrollElScrollWidth &&
    state.loadCustomersMediaErrorsCount.value < maxLoadCustomersMediaErrorsCount
  ) {
    try {
      const isLoaded = await loadMoreCustomersMedia(stateKey);

      // NOTE: need for fill all visible container with posts
      if (isLoaded) {
        loadCustomersMediaOnScroll(stateKey, scrollEl, scrollCoef);
      }
    } catch (e) {
      state.loadCustomersMediaErrorsCount.value += 1;

      if (
        state.loadCustomersMediaErrorsCount.value >=
        maxLoadCustomersMediaErrorsCount
      ) {
        console.error("Failed to load customers media");
        return;
      } else {
        loadCustomersMediaOnScroll(stateKey, scrollEl, scrollCoef);
      }
    }
  }
}

async function loadCustomersMediaOnPopupRequest() {
  await loadMoreCustomersMedia(activeStateKey.value);
}

onMounted(() => {
  checkScreenSize();
  window.addEventListener("resize", checkScreenSize);
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", scrollHandler);
  window.removeEventListener("resize", checkScreenSize);
});

const isMobileScreenSize = ref(false);

// Pagination and filters
const postCount = ref(props.propPostCount);
const perPage = postList.value.length;

function checkScreenSize() {
  if (document.body.clientWidth < 1024) {
    isMobileScreenSize.value = true;
  } else {
    isMobileScreenSize.value = false;
  }
}

watch(isMobileScreenSize, (isMobileNow, wasMobile) => {
  const isPopupOpen =
    Boolean(selectedPost.value) && selectedMediaIndex.value !== null;
  if (!isPopupOpen || isMobileNow === wasMobile) {
    return;
  }

  closePopup();
});

const selectedPage = ref(1);

const widgetMarketplaceReviews = ref<HTMLElement | null>(null);

const filterSegment = ref();
const filterSort = ref();
const filterRating = ref();

// NOTE: if need clear postLIst on filter update
watch(filterRating, () => {
  postList.value = [];
});

watch([filterSegment], async () => {
  selectedPage.value = 1;

  updatePostList(undefined, true);
});

watch(
  [filterRating, filterSort],
  async (
    [newFilterRating, newFilterSort],
    [oldFilterRating, oldFilterSort]
  ) => {
    const isRatingChanged = newFilterRating?.value !== oldFilterRating?.value;
    const isSortChanged =
      newFilterSort?.value !== oldFilterSort?.value ||
      newFilterSort?.sort !== oldFilterSort?.sort;

    if (isRatingChanged || isSortChanged) {
      selectedPage.value = 1;
      updatePostList(undefined, true);
      return;
    }

    updatePostList();
  }
);

async function updateAllCustomersMediaList(stateKey: TStateKey) {
  const state = stateKey === "modal" ? modalState : initialState;
  const { embedId, skuId, groupId, srcDomain } = props.settings;
  const lastPostId = state.allCustomersMediaList.value.at(-1)?.postId;
  state.hasActiveRequest.value = true;

  try {
    const newCustomersMediaList: TCustomersMediaList =
      await getCustomersMediaList(
        {
          embedId,
          skuId,
          groupId,
          srcDomain,
          lastPostId,
        },
        {
          orderBy: state.mediaSort.value.orderBy,
          sort: state.mediaSort.value.sort,
          current_variant: state.mediaSort.value.current_variant,
        }
      );

    if (
      state.customersMediaTotalCount.value === null &&
      typeof newCustomersMediaList?.mediaCount === "number"
    ) {
      state.customersMediaTotalCount.value = newCustomersMediaList.mediaCount;
    }

    if (!newCustomersMediaList?.imagesWithPostId) {
      return;
    }
    const newPostsMediaList: Array<imageWithPostId> =
      newCustomersMediaList.imagesWithPostId;

    const newPostListWithImages: Array<IMarketplaceReviewsPost> =
      newCustomersMediaList.postList;

    if (stateKey === "initial" && !state.bestPostIds.value.length) {
      const firstThreeBestPostIds = Array.from(
        new Set(
          newPostListWithImages
            .map((post) => post.instagramPost?.frisbuyId)
            .filter((postId): postId is number => typeof postId === "number")
        )
      ).slice(0, 3);

      state.bestPostIds.value = firstThreeBestPostIds;
    }

    addMediaToStateWithDedup(state, newPostsMediaList);
    newPostsMediaList.forEach((newMedia) => {
      const sameFullPost = newPostListWithImages.find((newPost) => {
        return newPost.instagramPost.frisbuyId === newMedia.postId;
      });

      if (!sameFullPost) {
        return;
      }

      const existedPostIndex = state.postListWithImages.value.findIndex(
        (post) => {
          return post.instagramPost.frisbuyId === newMedia.postId;
        }
      );

      if (existedPostIndex < 0) {
        state.postListWithImages.value.push(sameFullPost);
        return;
      }

      const existedPost = state.postListWithImages.value[existedPostIndex];
      const existedMediaList = Array.isArray(existedPost.media)
        ? existedPost.media
        : [];
      const incomingMediaList = Array.isArray(sameFullPost.media)
        ? sameFullPost.media
        : [];

      if (incomingMediaList.length <= existedMediaList.length) {
        return;
      }

      state.postListWithImages.value[existedPostIndex] = {
        ...existedPost,
        media: incomingMediaList,
      };
    });
  } catch (e) {
    // TODO: add types to Error `e` to use it to message
    throw Error("Failed to get CustomersMediaList");
  } finally {
    state.hasActiveRequest.value = false;
  }
}

function getCustomersMediaCursorKey(stateKey: TStateKey) {
  const state = stateKey === "modal" ? modalState : initialState;
  const lastPostId = state.allCustomersMediaList.value.at(-1)?.postId;
  return lastPostId ? String(lastPostId) : "__initial__";
}

function isHasMoreCustomersMedia(stateKey: TStateKey) {
  const state = stateKey === "modal" ? modalState : initialState;
  const totalMediaCount = state.customersMediaTotalCount.value;
  return (
    typeof totalMediaCount !== "number" ||
    state.allCustomersMediaList.value.length < totalMediaCount
  );
}

async function loadMoreCustomersMedia(stateKey: TStateKey) {
  const state = stateKey === "modal" ? modalState : initialState;

  if (state.hasActiveRequest.value || !isHasMoreCustomersMedia(stateKey)) {
    return false;
  }

  const cursorKey = getCustomersMediaCursorKey(stateKey);
  if (state.requestedCustomersMediaCursorSet.has(cursorKey)) {
    return false;
  }

  state.requestedCustomersMediaCursorSet.add(cursorKey);
  state.hasActiveRequest.value = true;

  try {
    await updateAllCustomersMediaList(stateKey);
    state.customersMediaScrollPage.value += 1;
    return true;
  } catch (e) {
    state.requestedCustomersMediaCursorSet.delete(cursorKey);
    throw e;
  } finally {
    state.hasActiveRequest.value = false;
  }
}

async function reloadCustomersMediaList(
  stateKey: TStateKey,
  orderBy: "provider_created" | "rating",
  sort: "asc" | "desc",
  currentVariant?: "true"
) {
  const state = stateKey === "modal" ? modalState : initialState;

  state.mediaSort.value = {
    orderBy,
    sort,
    current_variant: currentVariant,
  };
  state.allCustomersMediaList.value = [];
  state.customersMediaTotalCount.value = null;
  state.postListWithImages.value = [...state.propPostList.value];
  state.customersMediaScrollPage.value = 1;
  state.loadCustomersMediaErrorsCount.value = 0;
  state.hasActiveRequest.value = false;
  state.requestedCustomersMediaCursorSet.clear();

  await updateAllCustomersMediaList(stateKey);
}

async function updatePostList(
  page: number | undefined = undefined,
  isForceUpdate: boolean = false
) {
  const { embedId, skuId, groupId, srcDomain } = props.settings;
  const apiDefaults = { embedId, skuId, groupId, srcDomain };

  const pageForLoad = page || selectedPage.value;

  const selectedOrderBy = filterSort.value?.value;
  const selectedSort = filterSort.value?.sort;

  const filterOptions: TPostsFilterOptions = {
    page: pageForLoad,
    orgerBy: selectedOrderBy === "rating" ? "rating" : "provider_created",
    sort: selectedSort || "desc",
    ratingFilter: filterRating.value?.value,
    current_variant: filterSegment.value?.value,
  };

  // TODO: add request's error handler
  const postsInfo = await getPostsInfo(apiDefaults, filterOptions);
  const newPosts = postsInfo.posts;

  if (isForceUpdate) {
    postList.value = newPosts;
  }

  const pageIndex = perPage * (pageForLoad - 1);
  newPosts?.forEach((post: IMarketplaceReviewsPost, idx: number) => {
    postList.value[pageIndex + idx] = post;

    const hasMediaFlag = Boolean(post.instagramPost?.hasMedia);
    const hasRenderableMedia = Array.isArray(post.media)
      ? post.media.some((media) => {
          return hasValidFrisbuyMedia(media);
        })
      : false;

    if (!hasMediaFlag || !hasRenderableMedia) {
      return;
    }

    const isRealyNewPost = !initialState.postListWithImages.value.find(
      (postForFind) => {
        return (
          postForFind?.instagramPost.frisbuyId === post.instagramPost.frisbuyId
        );
      }
    );

    if (isRealyNewPost) {
      initialState.postListWithImages.value.push(post);
    }
  });

  postCount.value = postsInfo.postCount;
}

//#endregion get parsed post source

// Selecting post
const selectedPost = ref<IMarketplaceReviewsPost | null>(null);
const selectedPostIndex = ref<number | null>(null);
const selectedMediaIndex = ref<number | null>(null);
const selectedTextPost = ref<IMarketplaceReviewsPost | null>(null);

function selectPost(
  postId: number,
  media?: imageWithPostId,
  isInPopup: boolean = false,
  mediaIndexHint?: number,
  stateKey: TStateKey = activeStateKey.value,
  postHint?: IMarketplaceReviewsPost
) {
  activeStateKey.value = stateKey;
  const state = stateKey === "modal" ? modalState : initialState;

  const normalizeMediaUrl = (url?: string) => {
    if (!url) return "";

    return url
      .trim()
      .toLowerCase()
      .replace(/^https?:\/\/[^/]+/i, "")
      .replace(/[?#].*$/, "");
  };

  if (postId < 0) {
    console.error(`postIndex is ${postId}. Expected Number (>= 0)`);
    return;
  }

  let postById = state.postListWithImages.value.find(
    (post) => post?.instagramPost.frisbuyId === postId
  );

  let postIndex = state.postListWithImages.value.findIndex(
    (post) => post?.instagramPost.frisbuyId === postId
  );

  if (!postById && postHint) {
    postById = postHint;
  }

  if (!postById) {
    postById = postList.value.find(
      (post) => post?.instagramPost.frisbuyId === postId
    );
  }

  if (postById && postIndex < 0) {
    state.postListWithImages.value.push(postById);
    postIndex = state.postListWithImages.value.length - 1;
  }

  if (!postById) {
    console.error(`postId (${postId}) not found`);
    return;
  }

  const hasMediaFlag = Boolean(postById.instagramPost?.hasMedia);
  const hasRenderableMedia = Array.isArray(postById.media)
    ? postById.media.some((postMedia) => {
        return Boolean(
          postMedia?.thumbnailUrl ||
            postMedia?.imageUrl ||
            postMedia?.imageHighResolutionUrl ||
            postMedia?.videoUrl
        );
      })
    : false;
  const hasMedia = hasMediaFlag && hasRenderableMedia;
  if (!hasMedia) {
    selectedPost.value = postById;
    selectedPostIndex.value = postIndex;
    selectedMediaIndex.value = null;
    selectedTextPost.value = postById;
    return;
  }

  let mediaIndex = 0;
  if (
    typeof mediaIndexHint === "number" &&
    mediaIndexHint >= 0 &&
    mediaIndexHint < postById.media.length
  ) {
    mediaIndex = mediaIndexHint;
  } else if (media) {
    const mediaOrderByReference = state.allCustomersMediaList.value
      .filter((item) => item.postId === postId)
      .findIndex((item) => item === media);
    if (
      mediaOrderByReference >= 0 &&
      mediaOrderByReference < postById.media.length
    ) {
      mediaIndex = mediaOrderByReference;
    }

    const mediaThumb = normalizeMediaUrl(media.thumbnailUrl);
    const mediaHighres = normalizeMediaUrl(media.imageHighResolutionUrl);
    const mediaImage = normalizeMediaUrl(media.imageUrl);
    const mediaVideo = normalizeMediaUrl(media.videoUrl);

    if (mediaOrderByReference < 0) {
      const findedMediaIndex = postById.media.findIndex((postMedia) => {
        const postThumb = normalizeMediaUrl(postMedia.thumbnailUrl);
        const postHighres = normalizeMediaUrl(postMedia.imageHighResolutionUrl);
        const postImage = normalizeMediaUrl(postMedia.imageUrl);
        const postVideo = normalizeMediaUrl(postMedia.videoUrl);

        return (
          (postThumb && mediaThumb && postThumb === mediaThumb) ||
          (postHighres && mediaHighres && postHighres === mediaHighres) ||
          (postImage && mediaImage && postImage === mediaImage) ||
          (postVideo && mediaVideo && postVideo === mediaVideo)
        );
      });

      if (findedMediaIndex < 0) {
        const mediaOrderInPost = state.allCustomersMediaList.value
          .filter((item) => item.postId === postId)
          .findIndex((item) => {
            const itemThumb = normalizeMediaUrl(item.thumbnailUrl);
            const itemHighres = normalizeMediaUrl(item.imageHighResolutionUrl);
            const itemImage = normalizeMediaUrl(item.imageUrl);
            const itemVideo = normalizeMediaUrl(item.videoUrl);

            return (
              (itemThumb && mediaThumb && itemThumb === mediaThumb) ||
              (itemHighres && mediaHighres && itemHighres === mediaHighres) ||
              (itemImage && mediaImage && itemImage === mediaImage) ||
              (itemVideo && mediaVideo && itemVideo === mediaVideo)
            );
          });

        if (mediaOrderInPost >= 0 && mediaOrderInPost < postById.media.length) {
          mediaIndex = mediaOrderInPost;
        } else {
          console.warn(`media not found`);
        }
      } else {
        mediaIndex = findedMediaIndex;
      }
    }
  }

  const post = state.postListWithImages.value[postIndex];

  sendAnaliticEvent("ViewPic", {
    provPostId: post.instagramPost?.frisbuyId ?? post.providerPost.frisbuyId,
    fbProduct: post.frisbuyPost.id,
    igCode: post.instagramPost.shortcode,
    index: `[${mediaIndex}]`,
  });

  sendAnaliticEvent("ClickPic", {
    provPostId: post.instagramPost?.frisbuyId ?? post.providerPost.frisbuyId,
    fbProduct: post.frisbuyPost.id,
    igCode: post.instagramPost.shortcode,
  });

  selectedPost.value = postById;
  selectedPostIndex.value = postIndex;
  selectedTextPost.value = null;
  selectedMediaIndex.value = mediaIndex;

  if (isInPopup) {
    const PRELOAD_POSTS_BEFORE_END = 2;
    const shouldLoadMoreCustomersMedia =
      postIndex >=
      state.postListWithImages.value.length - 1 - PRELOAD_POSTS_BEFORE_END;

    if (shouldLoadMoreCustomersMedia) {
      loadMoreCustomersMedia(stateKey);
    }
  } else {
    // NOTE: preload next page to give opprtunity select nextPost on nextPage via Popup
    const pageWithSelectedPost = Math.floor(postIndex / perPage) + 1;
    if (postIndex === postList.value.length - 1) {
      updatePostList(pageWithSelectedPost + 1);
    }
  }
}

const prevPost = computed<IMarketplaceReviewsPost | undefined>(() => {
  if (!selectedPostIndex.value) {
    return undefined;
  }
  return activeState.value.postListWithImages.value[
    selectedPostIndex.value - 1
  ];
});
function selectPrevPost() {
  if (!prevPost.value) {
    return;
  }

  selectPost(
    prevPost.value.instagramPost.frisbuyId,
    undefined,
    false,
    undefined,
    activeStateKey.value
  );
}

const nextPost = computed<IMarketplaceReviewsPost | undefined>(() => {
  if (typeof selectedPostIndex.value !== "number") {
    return undefined;
  }
  return activeState.value.postListWithImages.value[
    selectedPostIndex.value + 1
  ];
});
async function selectNextPost() {
  if (!nextPost.value) {
    return;
  }

  selectPost(
    nextPost.value.instagramPost.frisbuyId,
    undefined,
    true,
    undefined,
    activeStateKey.value
  );
}

function closePopup() {
  selectedPost.value = null;
  selectedMediaIndex.value = null;
  selectedTextPost.value = null;
}

async function ensureModalStateLoaded() {
  if (isModalStateLoaded.value || isModalStateLoading.value) {
    return;
  }

  isModalStateLoading.value = true;

  const { embedId, skuId, groupId, srcDomain } = props.settings;
  const apiDefaults = { embedId, skuId, groupId, srcDomain };
  const filterOptions: TPostsFilterOptions = {
    page: 1,
    orgerBy: "provider_created",
    sort: "desc",
  };

  try {
    const postsInfo = await getPostsInfo(apiDefaults, filterOptions);
    const modalPosts = (postsInfo?.posts || []) as IMarketplaceReviewsPost[];

    modalState.propPostList.value = modalPosts;
    modalState.postCount.value = postsInfo?.postCount || modalPosts.length;
    modalState.postListWithImages.value = [...modalPosts];

    await updateAllCustomersMediaList("modal");
    isModalStateLoaded.value = true;
  } finally {
    isModalStateLoading.value = false;
  }
}

async function openFullyWidget() {
  await ensureModalStateLoaded();
  isShowFullyWidget.value = true;
}

function closeFullyWidget() {
  isShowFullyWidget.value = false;
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (props.settings.gallery.header.html)
      ? (_openBlock(), _createElementBlock("div", {
          key: 0,
          innerHTML: props.settings.gallery.header.html
        }, null, 8, _hoisted_1))
      : _createCommentVNode("", true),
    _createVNode(ContentWrapper, {
      settings: props.settings,
      propPostList: props.propPostList,
      lastPage: props.lastPage,
      propPostCount: props.propPostCount,
      allCustomersMediaList: _unref(initialState).allCustomersMediaList.value,
      customersMediaTotalCount: _unref(initialState).customersMediaTotalCount.value,
      isLoadingCustomersMedia: _unref(initialState).hasActiveRequest.value,
      productRating: props.productRating,
      extraData: props.extraData,
      isMobileScreenSize: isMobileScreenSize.value,
      isShowFullyWidgetLoading: isModalStateLoading.value,
      onSelectPost: _cache[0] || (_cache[0] = (postId, media, postHint, mediaIndexHint) => selectPost(postId, media, false, mediaIndexHint, 'initial', postHint)),
      onSendAnaliticEvent: sendAnaliticEvent,
      onShowFullyWidget: openFullyWidget
    }, null, 8, ["settings", "propPostList", "lastPage", "propPostCount", "allCustomersMediaList", "customersMediaTotalCount", "isLoadingCustomersMedia", "productRating", "extraData", "isMobileScreenSize", "isShowFullyWidgetLoading"]),
    (isShowFullyWidget.value)
      ? (_openBlock(), _createBlock(FullyWidgetContainerPopup, {
          key: 1,
          onClose: closeFullyWidget
        }, {
          content: _withCtx(() => [
            _createVNode(ContentWrapper, {
              settings: props.settings,
              propPostList: _unref(modalState).propPostList.value,
              lastPage: props.lastPage,
              propPostCount: _unref(modalState).postCount.value,
              allCustomersMediaList: _unref(modalState).allCustomersMediaList.value,
              customersMediaTotalCount: _unref(modalState).customersMediaTotalCount.value,
              isLoadingCustomersMedia: _unref(modalState).hasActiveRequest.value,
              productRating: props.productRating,
              extraData: props.extraData,
              isMobileScreenSize: isMobileScreenSize.value,
              isShowFullyWidgetLoading: isModalStateLoading.value,
              isFullyWidget: "",
              onSelectPost: _cache[1] || (_cache[1] = (postId, media, postHint, mediaIndexHint) => selectPost(postId, media, false, mediaIndexHint, 'modal', postHint)),
              onChangeMediaSort: _cache[2] || (_cache[2] = (orderBy, sort, currentVariant) => reloadCustomersMediaList('modal', orderBy, sort, currentVariant)),
              onMergeMediaFromPosts: _cache[3] || (_cache[3] = (posts) => mergeMediaFromPostsToState('modal', posts)),
              onSendAnaliticEvent: sendAnaliticEvent
            }, null, 8, ["settings", "propPostList", "lastPage", "propPostCount", "allCustomersMediaList", "customersMediaTotalCount", "isLoadingCustomersMedia", "productRating", "extraData", "isMobileScreenSize", "isShowFullyWidgetLoading"])
          ]),
          _: 1
        }))
      : _createCommentVNode("", true),
    (selectedPost.value && selectedMediaIndex.value !== null && !isMobileScreenSize.value)
      ? (_openBlock(), _createBlock(PopupReviewPost, {
          key: 2,
          post: selectedPost.value,
          selectedPostIndex: selectedPostIndex.value || 0,
          selectedMediaIndex: selectedMediaIndex.value,
          showLeftArrow: Boolean(prevPost.value),
          showRightArrow: Boolean(nextPost.value),
          settings: props.settings,
          allCustomersMediaList: activeState.value.allCustomersMediaList.value,
          totalMediaCount: activeState.value.customersMediaTotalCount.value ?? undefined,
          reviewDate: _unref(postExtractor).getParsedReviewDate(selectedPost.value),
          reviewTime: _unref(postExtractor).getParsedReviewTime(selectedPost.value),
          onSendAnaliticEvent: _cache[4] || (_cache[4] = (event, params) => sendAnaliticEvent(event, params)),
          onSelectPrevPost: selectPrevPost,
          onSelectNextPost: selectNextPost,
          onSelectPost: _cache[5] || (_cache[5] = (postId, media, mediaIndexHint) => selectPost(postId, media, false, mediaIndexHint, activeStateKey.value)),
          onRequestToLoadCustomersMedia: _cache[6] || (_cache[6] = () => loadCustomersMediaOnPopupRequest()),
          onClose: closePopup
        }, null, 8, ["post", "selectedPostIndex", "selectedMediaIndex", "showLeftArrow", "showRightArrow", "settings", "allCustomersMediaList", "totalMediaCount", "reviewDate", "reviewTime"]))
      : _createCommentVNode("", true),
    (selectedPost.value && selectedMediaIndex.value !== null && isMobileScreenSize.value)
      ? (_openBlock(), _createBlock(PopupReviewPostMobile, {
          key: 3,
          post: selectedPost.value,
          selectedPostIndex: selectedPostIndex.value || 0,
          selectedMediaIndex: selectedMediaIndex.value,
          bestPostIds: _unref(initialState).bestPostIds.value,
          allCustomersMediaList: activeState.value.allCustomersMediaList.value,
          totalMediaCount: activeState.value.customersMediaTotalCount.value ?? undefined,
          reviewDate: _unref(postExtractor).getParsedReviewDate(selectedPost.value),
          reviewTime: _unref(postExtractor).getParsedReviewTime(selectedPost.value),
          onSendAnaliticEvent: _cache[7] || (_cache[7] = (event, params) => sendAnaliticEvent(event, params)),
          onSelectPrevPost: selectPrevPost,
          onSelectNextPost: selectNextPost,
          onSelectPost: _cache[8] || (_cache[8] = (postId, media, mediaIndexHint) => selectPost(postId, media, true, mediaIndexHint, activeStateKey.value)),
          onRequestToLoadCustomersMedia: _cache[9] || (_cache[9] = () => loadCustomersMediaOnPopupRequest()),
          onClose: closePopup
        }, null, 8, ["post", "selectedPostIndex", "selectedMediaIndex", "bestPostIds", "allCustomersMediaList", "totalMediaCount", "reviewDate", "reviewTime"]))
      : _createCommentVNode("", true),
    (selectedTextPost.value)
      ? (_openBlock(), _createBlock(PopupReviewText, {
          key: 4,
          post: selectedTextPost.value,
          selectedPostIndex: selectedPostIndex.value || 0,
          reviewDate: _unref(postExtractor).getParsedReviewDate(selectedTextPost.value),
          onClose: closePopup
        }, null, 8, ["post", "selectedPostIndex", "reviewDate"]))
      : _createCommentVNode("", true)
  ], 64))
}
}

})